import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import flatten from 'lodash/flatten';
import isArray from 'lodash/isArray';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { useAppsStore } from '@/stores/apps.store';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { formatDates, createTimeSeriesFromDate } from '@/common/time-series-picker/time-series-picker.util.js';
import { AppsService } from '@/common/apps/apps.service';
import { VanillaDateRange } from '@/data/vanilla-date-range';
import { APP_PLATFORM_LIST } from '@/utils/apps';
import {
    getDefaultSelectedApp,
    getDefaultSelectedChannel,
    defaultSelectedFeatureType,
    defaultSelectedAccount,
} from '@/stores/helpers/global-filters.helper';

import { useSubscriptionStore } from '@/stores/subscription.store';

const isProduction = process.env.NODE_ENV === 'production';

interface AppSelection {
    id: string;
    displayName?: string;
    eventRate?: number;
    latestEventTime?: number;
    [key: string]: unknown;
}

interface AccountSelection {
    account?: unknown;
    [key: string]: unknown;
}

interface TimeSeries {
    config: {
        type?: string;
        [key: string]: unknown;
    };
    label?: string;
    spec?: unknown;
    [key: string]: unknown;
}

interface StoredValue<T> {
    value?: T;
}

interface ParsedSegment {
    blacklist?: string;
    includeAnonymous?: boolean;
    includeDNT?: boolean;
    segmentId?: string;
}

interface GlobalFiltersState {
    timeSeries: TimeSeries;
    selectedSegmentId?: string;
    selectedAccount?: AccountSelection;
    selectedApp?: AppSelection;
    selectedApps?: AppSelection[];
    selectedChannel: unknown;
    selectedLanguage?: string | null;
    selectedVerification: unknown;
    starredMap: Record<string, boolean>;
    allTime: boolean;
    selectedFeatureType: unknown;
}

function warn (warning: string) {
    if (!isProduction) {
        // eslint-disable-next-line no-console
        console.warn(warning);
    }
}

const ACCOUNT_STORAGE_KEY = 'SELECTED_ACCOUNT_KEY';
const APP_STORAGE_KEY = 'app-chooser--storage-key';
const APPS_STORAGE_KEY = 'multi-app-chooser--storage-key';
const CHANNEL_STORAGE_KEY = 'SELECTED_CHANNEL_KEY';
const ALL_TIME_STORAGE_KEY = 'ALL_TIME_STORAGE_KEY';
const FEATURE_TYPE_KEY = 'FEATURE_TYPE_KEY';
const VERIFICATION_KEY = 'VERIFICATION_KEY';
const DEFAULT_DATE = { type: 'Last 30 Days' };
const DEFAULT_DATE_NEW = { type: 'Today' };
const GLOBAL_FILTER_DATE_KEY = 'aggregationTimeRange';
const GLOBAL_FILTER_SEGMENT_KEY = 'aggregationSegmentId';

function getSegmentsStore () {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('@/stores/segments.store').useSegmentsStore();
}

function getPerSubscriptionUserStorage () {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('@/utils/storage').PerSubscriptionUserStorage;
}

function getSegmentParams (segmentId?: string): ParsedSegment {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('@/utils/segments').parseSegmentParams({ segmentId }) as ParsedSegment;
}

function getAppChooserService () {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('@/common/app-chooser/app-chooser.service.js').AppChooserService;
}

function transformAggAppId (appId: unknown): unknown {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('@/apps/apps.utils').transformAggAppIdIntoBareAppId(appId);
}

export const useGlobalFiltersStore = defineStore('globalFilters', {
    state: (): GlobalFiltersState => ({
        timeSeries: {} as TimeSeries,
        selectedSegmentId: undefined,
        selectedAccount: undefined,
        selectedApp: undefined,
        selectedApps: undefined,
        selectedChannel: undefined,
        selectedLanguage: undefined,
        selectedVerification: undefined,
        starredMap: {} as Record<string, boolean>,
        allTime: false,
        selectedFeatureType: undefined,
    }),
    getters: {
        // Note: the Vuex getter named "selectedApp" returned state.selectedApps (intentional naming quirk).
        // In Pinia "selectedApp" refers to the state field of that name; consumers of the old getter
        // must read store.selectedApps directly.
        selectedSegment (state): unknown {
            return getSegmentsStore().segmentById(state.selectedSegmentId as string);
        },
        segmentBlacklist (state): string {
            const segmentId = state.selectedSegmentId;

            return getSegmentParams(segmentId).blacklist || 'apply';
        },
        segmentIncludeAnonymous (state): boolean | undefined {
            const segmentId = state.selectedSegmentId;

            return getSegmentParams(segmentId).includeAnonymous;
        },
        segmentIncludesDNT (state): boolean | undefined {
            const segmentId = state.selectedSegmentId;

            return getSegmentParams(segmentId).includeDNT;
        },
        segmentId (state): string | undefined {
            const segmentId = state.selectedSegmentId;

            return getSegmentParams(segmentId).segmentId;
        },
        timeSeriesSpec (state): unknown {
            return state.timeSeries.spec;
        },
        selectedAccountAccount (state): unknown {
            return get(state.selectedAccount, 'account');
        },
        appId (state): unknown {
            if (!state.selectedApps) return;

            const appIds = state.selectedApps.map((app) =>
                (getAppChooserService() as { getAppIds: (selection: AppSelection) => unknown }).getAppIds(app),
            );

            // "all mobile apps" turns into ['expandAppIds("IOS")', 'expandAppIds("android")']
            // we need to make sure to flatten that down to a single array
            return flatten(appIds);
        },
        nonAggAppId (): unknown {
            const { appId } = this;

            if (!appId || (Array.isArray(appId) && appId.length === 0)) return ['*'];

            // for backwards compat, appId is only an array when it contains a distinct selection of app ids
            // a single app selection, as well as selection shorthand like expandAppId('*') are not arrays
            if (isArray(appId)) {
                return (appId as string[]).map(transformAggAppId);
            }

            return transformAggAppId(appId);
        },
        starredByType (state) {
            return (type: string) => !!(type && state.starredMap[type]);
        },
        selectedAppEventRate (state): number {
            const apps = state.selectedApps;
            if (!apps?.length) return 1;

            const eventRates = apps.map((app) => app.eventRate) as number[];
            const maxRate = Math.max.apply(null, eventRates);

            return maxRate || 1;
        },
        selectedAppLatestEventTime (state): number {
            const apps = state.selectedApps;
            if (!apps?.length) return -1;

            const eventTime = apps.map((app) => app.latestEventTime) as number[];
            const maxTime = Math.max.apply(null, eventTime);

            return maxTime || -1;
        },
    },
    actions: {
        setTimeSeries ({ timeSeries }: { timeSeries: TimeSeries }) {
            const DateRange = VanillaDateRange as unknown as new (config: TimeSeries['config']) => {
                toString: () => string;
                timeSeries: () => unknown;
            };
            const dateRange = new DateRange(timeSeries.config);
            timeSeries.label = dateRange.toString();
            timeSeries.spec = dateRange.timeSeries();
            this.timeSeries = timeSeries;
        },
        setSelectedSegmentId ({ selectedSegmentId, updateAngular }: { selectedSegmentId: string | undefined; updateAngular?: boolean }) {
            this.selectedSegmentId = selectedSegmentId;

            if (updateAngular) {
                if (selectedSegmentId) {
                    return getPerSubscriptionUserStorage().set(GLOBAL_FILTER_SEGMENT_KEY, selectedSegmentId);
                }

                return getPerSubscriptionUserStorage().remove(GLOBAL_FILTER_SEGMENT_KEY);
            }

            return undefined;
        },
        setSelectedChannel (
            { selectedChannel = getDefaultSelectedChannel() }: { selectedChannel?: unknown; updateAngular?: boolean } = {},
        ) {
            this.selectedChannel = selectedChannel;
        },
        setSelectedApp ({ selectedApp = getDefaultSelectedApp(), updateAngular }: { selectedApp?: AppSelection; updateAngular?: boolean }) {
            this.selectedApp = selectedApp as AppSelection;

            if (updateAngular) {
                return getPerSubscriptionUserStorage().set(APP_STORAGE_KEY, selectedApp);
            }

            return undefined;
        },
        setSelectedApps ({ selectedApps = [getDefaultSelectedApp()], updateAngular }: { selectedApps?: AppSelection[]; updateAngular?: boolean }) {
            this.selectedApps = selectedApps as AppSelection[] | undefined;

            if (updateAngular) {
                // only save app id to user settings
                const selectedAppsIds = selectedApps?.map((app) => ({ id: app.id }));

                return getPerSubscriptionUserStorage().set(APPS_STORAGE_KEY, selectedAppsIds);
            }

            return undefined;
        },
        setSelectedAccount ({ selectedAccount = defaultSelectedAccount, updateAngular }: { selectedAccount?: AccountSelection; updateAngular?: boolean }) {
            this.selectedAccount = selectedAccount as AccountSelection;
            if (updateAngular) {
                return getPerSubscriptionUserStorage().set(ACCOUNT_STORAGE_KEY, selectedAccount.account);
            }

            return undefined;
        },
        setSelectedLanguage ({ selectedLanguage = null }: { selectedLanguage?: string | null } = {}) {
            this.selectedLanguage = selectedLanguage;
            // Not using updateAngular pattern from previous examples,
            // language-filter.vue directly calls PerSubscriptionUserStorage
        },
        setVerification ({ verification = null, persist }: { verification?: unknown; persist?: boolean } = {}) {
            this.selectedVerification = verification;

            if (persist) {
                getPerSubscriptionUserStorage().set(VERIFICATION_KEY, verification);
            }
        },
        setStarred ({ starred = false, filterType, updateAngular }: { starred?: boolean; filterType?: string; updateAngular?: boolean } = {}) {
            let resolvedFilterType = filterType;
            if (!resolvedFilterType) {
                warn('The global star filter is deprecated. Please specify a unique filterType identifier.');
                resolvedFilterType = 'default';
            }

            this.starredMap[resolvedFilterType] = starred;
            if (updateAngular) {
                return getPerSubscriptionUserStorage().set('starsFilterMap', this.starredMap);
            }

            return undefined;
        },
        setStarredMap ({ map = {} }: { map?: Record<string, boolean> } = {}) {
            this.starredMap = map;
        },
        setAllTime ({ allTime }: { allTime: boolean }) {
            this.allTime = allTime;

            return getPerSubscriptionUserStorage().set(ALL_TIME_STORAGE_KEY, allTime);
        },
        setSelectedFeatureType ({ featureType }: { featureType: unknown }) {
            this.selectedFeatureType = featureType;

            return getPerSubscriptionUserStorage().set(FEATURE_TYPE_KEY, featureType);
        },
        loadInitialStateFromUserSettings () {
            const segmentIdSetting = useUserSettingsStore().settingByName(GLOBAL_FILTER_SEGMENT_KEY, true);
            const timeSeriesSetting = useUserSettingsStore().settingByName(GLOBAL_FILTER_DATE_KEY, true);
            const selectedSegmentId = (segmentIdSetting as StoredValue<string> | undefined)?.value || 'everyone';

            this.setSelectedSegmentId({ selectedSegmentId });
            this.loadSelectedAccount();
            this.loadSelectedChannel();

            let globalFilterDate = (timeSeriesSetting as StoredValue<TimeSeries['config']> | undefined)?.value;
            if (!(timeSeriesSetting as StoredValue<TimeSeries['config']> | undefined)?.value) {
                if (Date.now() - (useSubscriptionStore().active.firstEventTime || 0) < 23 * 60 * 60 * 1000) {
                    globalFilterDate = DEFAULT_DATE_NEW;
                } else {
                    globalFilterDate = DEFAULT_DATE;
                }
            }

            const resolvedGlobalFilterDate = globalFilterDate || DEFAULT_DATE;
            const datesFromUserSetting = formatDates(
                resolvedGlobalFilterDate as Parameters<typeof formatDates>[0],
            );
            const timeSeries = createTimeSeriesFromDate(datesFromUserSetting, false, true);
            if (resolvedGlobalFilterDate.type === 'Custom') {
                timeSeries.config.type = 'Custom';
            }
            this.setTimeSeries({ timeSeries });
        },
        updateSelectedTimeSeries ({ timeSeries }: { timeSeries: TimeSeries }) {
            this.setTimeSeries({ timeSeries });
            useUserSettingsStore().update({ name: GLOBAL_FILTER_DATE_KEY, namespaced: true, value: timeSeries.config });
        },
        loadSelectedAccount () {
            return getPerSubscriptionUserStorage().get(ACCOUNT_STORAGE_KEY).then(({ value }: StoredValue<unknown>) => {
                const savedSelectedAccount = value;
                this.setSelectedAccount({
                    selectedAccount: {
                        account: savedSelectedAccount || defaultSelectedAccount,
                    },
                    updateAngular: false,
                });
            });
        },
        loadSelectedChannel () {
            return getPerSubscriptionUserStorage().get(CHANNEL_STORAGE_KEY).then(({ value }: StoredValue<unknown>) => {
                this.setSelectedChannel({
                    selectedChannel: value || getDefaultSelectedChannel(),
                    updateAngular: false,
                });
            });
        },
        async loadSelectedApp () {
            const appsStore = useAppsStore();
            let { value } = await getPerSubscriptionUserStorage().get(APP_STORAGE_KEY) as StoredValue<AppSelection | null>;
            const appsService = AppsService as unknown as { getAppGroupLabelByAggId: (id: string) => string | undefined };
            if (value && !appsService.getAppGroupLabelByAggId(value.id)) {
                // if the saved app isn't a default option, we check that it still exists
                await appsStore.loadAll();
                if (!appsStore.getAppById(value.id)) {
                    value = null;
                }
            }
            this.setSelectedApp({
                selectedApp: value || getDefaultSelectedApp(),
                updateAngular: false,
            });
        },
        async loadSelectedApps () {
            if (this.selectedApps) {
                return;
            }

            const appsStore = useAppsStore();
            await appsStore.loadAll();

            const { value: apps } = await getPerSubscriptionUserStorage().get(APPS_STORAGE_KEY) as StoredValue<AppSelection[]>;
            const validApps: AppSelection[] =
                apps?.filter(({ id }) => APP_PLATFORM_LIST.includes(id) || appsStore.getAppById(id)) || [];

            if (!validApps.length) {
                this.setSelectedApps({
                    selectedApps: [getDefaultSelectedApp()],
                    updateAngular: false,
                });

                return;
            }

            const appsService = AppsService as unknown as { getAppGroupLabelByAggId: (id: string) => string | undefined };
            const platformOption = appsService.getAppGroupLabelByAggId(validApps[0]?.id);
            const selectedApps = validApps.map(({ id }) => {
                if (platformOption) {
                    return { id, displayName: platformOption };
                }

                return appsStore.getAppById(id);
            });

            this.setSelectedApps({
                selectedApps,
                updateAngular: false,
            });
        },
        loadSelectedStarred () {
            return getPerSubscriptionUserStorage().get('starsFilterMap').then((setting: StoredValue<Record<string, boolean>>) => {
                // PSUS returns the literal Vuex state value, which for complex objects is pass-by-reference.
                // We want a separate copy of the map, so that our changes don't cause a reactive cascade.
                const storedMap = setting.value as Record<string, boolean> | undefined;
                const map = cloneDeep(storedMap) || {};
                this.setStarredMap({ map });
            });
        },
        loadSelectedLanguage () {
            return getPerSubscriptionUserStorage().get('selectedLanguage').then((setting: StoredValue<string | null>) => {
                const selectedLanguage = setting.value as string | null | undefined;
                this.setSelectedLanguage({ selectedLanguage });
            });
        },
        async loadVerification () {
            const { value } = await getPerSubscriptionUserStorage().get(VERIFICATION_KEY) as StoredValue<unknown>;
            this.setVerification({
                verification: value || 'all',
            });
        },
        async loadAllTime () {
            let { value } = await getPerSubscriptionUserStorage().get(ALL_TIME_STORAGE_KEY) as StoredValue<boolean>;
            if (value === undefined || value === null) {
                value = true;
            }
            this.setAllTime({ allTime: value });
        },
        resetSelectedApps () {
            this.setSelectedApps({
                selectedApps: undefined,
                updateAngular: true,
            });
        },
        async loadSelectedFeatureType () {
            const { value } = await getPerSubscriptionUserStorage().get(FEATURE_TYPE_KEY) as StoredValue<unknown>;
            this.setSelectedFeatureType({
                featureType: value || defaultSelectedFeatureType,
            });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useGlobalFiltersStore, module.hot));
}
